import { useState, useEffect, useCallback } from 'react';
import { Profile } from '@/types/profile';
import { selectProfileIcon } from '@/utils/profileIcons';
import { getPayloadById } from '@/utils/payloads';
import { toast } from 'sonner';

// Initial mock profile data
const initialMockProfiles: Profile[] = [
  {
    id: "1",
    name: "Basic Security Profile",
    description: "Essential security settings for all devices",
    platforms: ["macos", "ios", "ipados"],
    payloads: [
      { payloadId: "passcode", configValues: { minLength: 6, allowSimple: false } },
      { payloadId: "restrictions", configValues: { allowCamera: true, forceEncryptedBackup: true } }
    ],
    customer: "c1",
    customers: ["c1"],
    groups: ["g1", "g2"],
    tags: ["t3"],
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: "2",
    name: "Corporate Wi-Fi",
    description: "Automatically connect to corporate Wi-Fi networks",
    platforms: ["macos", "ios", "ipados", "tvos"],
    payloads: [
      { payloadId: "wifi", configValues: { ssid: "Corporate-WiFi", security: "WPA2" } }
    ],
    customer: "c3",
    customers: ["c3"],
    groups: ["g6", "g7"],
    tags: ["t2", "t5"],
    createdAt: new Date(),
    updatedAt: new Date()
  }
];

// Migrate profiles to add icons if they don't have one
const migrateProfileIcons = (profiles: Profile[]): Profile[] => {
  return profiles.map(profile => {
    if (!profile.icon) {
      const payloadObjects = profile.payloads
        .map(p => getPayloadById(p.payloadId))
        .filter(Boolean) as any[];
      
      return {
        ...profile,
        icon: selectProfileIcon(payloadObjects)
      };
    }
    return profile;
  });
};

// Store profiles in session storage to persist between page navigations
const getStoredProfiles = (): Profile[] => {
  if (typeof window === 'undefined') return initialMockProfiles;
  
  const stored = sessionStorage.getItem('profiles');
  if (!stored) {
    const migratedProfiles = migrateProfileIcons(initialMockProfiles);
    sessionStorage.setItem('profiles', JSON.stringify(migratedProfiles));
    return migratedProfiles;
  }
  
  try {
    const parsedProfiles = JSON.parse(stored);
    const migratedProfiles = migrateProfileIcons(parsedProfiles);
    
    // Re-save if any profiles were migrated
    if (migratedProfiles.some((p, i) => p.icon !== parsedProfiles[i]?.icon)) {
      sessionStorage.setItem('profiles', JSON.stringify(migratedProfiles));
    }
    
    return migratedProfiles;
  } catch (error) {
    console.error("Error parsing stored profiles:", error);
    return initialMockProfiles;
  }
};

export const useProfiles = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        // In a real app, this would be an API call
        setProfiles(getStoredProfiles());
      } catch (error) {
        console.error("Error fetching profiles:", error);
        setError(error instanceof Error ? error : new Error("Failed to fetch profiles"));
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  const addProfile = useCallback((profile: Omit<Profile, "id" | "createdAt" | "updatedAt">, isDraft = false) => {
    // Create a new profile with generated ID and timestamps
    const newProfile: Profile = {
      ...profile,
      id: `profile-${Date.now()}`, // Generate a simple unique ID
      createdAt: new Date(),
      updatedAt: new Date(),
      isDraft: isDraft
    };

    const updatedProfiles = [...profiles, newProfile];
    setProfiles(updatedProfiles);
    
    // Save to session storage
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('profiles', JSON.stringify(updatedProfiles));
    }
    
    toast.success(isDraft ? "Profile saved as draft" : "Profile created successfully");
    return newProfile;
  }, [profiles]);

  const updateProfile = useCallback((id: string, profileData: Partial<Profile>) => {
    const updatedProfiles = profiles.map(profile => 
      profile.id === id 
        ? { ...profile, ...profileData, updatedAt: new Date() } 
        : profile
    );
    
    setProfiles(updatedProfiles);
    
    // Save to session storage
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('profiles', JSON.stringify(updatedProfiles));
    }
    
    toast.success("Profile updated successfully");
  }, [profiles]);

  const deleteProfile = useCallback((id: string) => {
    const updatedProfiles = profiles.filter(profile => profile.id !== id);
    setProfiles(updatedProfiles);
    
    // Save to session storage
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('profiles', JSON.stringify(updatedProfiles));
    }
    
    toast.success("Profile deleted successfully");
  }, [profiles]);

  return {
    profiles,
    loading,
    error,
    addProfile,
    updateProfile,
    deleteProfile
  };
};
